import React, { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari';
import { versionsPanelEntryPoint } from '@atlassian/jira-software-version-backlog-panel-relay/entrypoint';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useBoardAri } from '../../../common/hooks/use-board-ari';
import { versionsPanelLoad } from '../../../services/utils/performance-analytics';
import { VersionPanelEntrypointContext } from '../../../services/versions-panel-entrypoint';
import { useBacklogSelector } from '../../../state';
import { getProjectIds } from '../../../state/selectors/app';
import { useIsVersionsPanelOpen } from '../../../state/state-hooks/use-is-versions-panel-open';

export const VersionPanelEntrypointContextProvider = ({ children }: { children: ReactNode }) => {
	const cloudId = useCloudId();
	const isFirstRender = useRef(true);
	const isLoaded = useRef(false);

	const isVersionPanelOpen = useIsVersionsPanelOpen();

	const projectIds = useBacklogSelector(getProjectIds);
	const jiraProjectIds = projectIds.map((id) =>
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'project',
			resourceId: String(id),
		}),
	);

	const boardARI = useBoardAri();

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			after: null,
			first: 500,
			jiraProjectIds,
			boardId: boardARI,
			shouldShowVersionPanel: !!jiraProjectIds.length,
		}),
		[boardARI, cloudId, jiraProjectIds],
	);

	const entryPointHookResult = useEntryPoint(
		versionsPanelEntryPoint,
		entryPointParams,
		versionsPanelLoad,
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (!isLoaded.current && isVersionPanelOpen) {
			entryPointHookResult.entryPointActions.load();
			isLoaded.current = true;
		} else if (isFirstRender.current) {
			// if panel is open on first render - load versions panel
			if (isVersionPanelOpen) {
				entryPointHookResult.entryPointActions.load();
			}

			isFirstRender.current = false;
		}
	}, [entryPointHookResult, isVersionPanelOpen]);

	return (
		<VersionPanelEntrypointContext.Provider value={entryPointHookResult}>
			{children}
		</VersionPanelEntrypointContext.Provider>
	);
};
